import { clearTokens } from '@/utils/auth-token';

/** 每次登录/登出递增，供 toast dedupe 与 deferred teardown 竞态防护 */
let authGeneration = 0;

const teardownFns = new Set();

let deferredTeardownPromise = null;

export function getAuthGeneration() {
  return authGeneration;
}

/** 新会话建立时调用，使进行中的 logout deferred teardown 失效 */
export function bumpAuthGeneration() {
  authGeneration += 1;
}

export function registerAuthTeardown(fn) {
  if (typeof fn !== 'function') return () => {};
  teardownFns.add(fn);
  return () => teardownFns.delete(fn);
}

/**
 * 延迟 import，避免 auth-session 初始化前被 reset-feed-stores → useMarketWatchlist 拉回。
 * 全程对照 authGeneration：若中途又登录，中止后续破坏性清理。
 */
async function runDeferredTeardown(expectedGeneration) {
  const stillCurrent = () => expectedGeneration === authGeneration;

  const [
    { resetUserFeedOnLogout },
    { invalidatePlatformBalanceCache },
    { invalidateUsdcBalanceCache },
    { stopDepositWatch },
    { usePortfolioBalanceStore },
    { useFiatDepositStore },
    { useDepositOptionsStore },
    { resetDepositGeoSession },
    { resetChinaMainlandRegion },
    { resetUsFiatRestriction },
    { resetVietnamPayIpAllowed },
    { resetBrazilPayIpAllowed },
    { resetMexicoPayIpAllowed },
    { resetIndonesiaPayIpAllowed },
    { clearDepositItemsStorage },
    { clearFiatCheckoutSessionStorage },
    { clearTradingReadyCache },
    { clearPrivyPendingAuth },
    { usePrivySignSessionStore },
    { useCustodialSessionStore },
  ] = await Promise.all([
    import('@/utils/reset-feed-stores'),
    import('@/api/withdraw'),
    import('@/utils/wallet-balance-summary'),
    import('@/composables/useDepositStatus'),
    import('@/stores/portfolioBalance'),
    import('@/stores/fiatDeposit'),
    import('@/stores/depositOptions'),
    import('@/utils/deposit-geo-session'),
    import('@/utils/china-mainland-region'),
    import('@/utils/deposit-us-fiat-guard'),
    import('@/utils/deposit-vietnam-pay-guard'),
    import('@/utils/deposit-brazil-pay-guard'),
    import('@/utils/deposit-mexico-pay-guard'),
    import('@/utils/deposit-indonesia-pay-guard'),
    import('@/utils/deposit-item-queue'),
    import('@/utils/deposit-onramp-checkout'),
    import('@/utils/privy-trading-ready'),
    import('@/utils/privy-pending-auth'),
    import('@/stores/privySignSession'),
    import('@/stores/custodialSession'),
  ]);

  if (!stillCurrent()) return;

  clearPrivyPendingAuth();
  resetDepositGeoSession();
  resetChinaMainlandRegion();
  resetUsFiatRestriction();
  resetVietnamPayIpAllowed();
  resetBrazilPayIpAllowed();
  resetMexicoPayIpAllowed();
  resetIndonesiaPayIpAllowed();
  if (!stillCurrent()) return;

  resetUserFeedOnLogout();
  invalidatePlatformBalanceCache();
  invalidateUsdcBalanceCache();
  stopDepositWatch();
  clearDepositItemsStorage();
  clearFiatCheckoutSessionStorage();
  clearTradingReadyCache();
  try {
    usePrivySignSessionStore().reset();
  } catch {
    /* ignore */
  }
  try {
    useCustodialSessionStore().reset();
  } catch {
    /* ignore */
  }

  if (!stillCurrent()) return;

  usePortfolioBalanceStore().reset();
  if (!stillCurrent()) return;
  useFiatDepositStore().reset();
  if (!stillCurrent()) return;
  useDepositOptionsStore().reset();

  if (!stillCurrent()) return;

  // Firebase Google 会话：避免下一访客 One Tap 自动带上上一账号
  try {
    const { isFirebaseConfigured } = await import('@/utils/firebase-config');
    if (isFirebaseConfigured() && stillCurrent()) {
      const { getFirebaseAuth, disableGoogleOneTapAutoSelect } = await import('@/utils/firebase');
      disableGoogleOneTapAutoSelect();
      const { signOut } = await import('firebase/auth');
      await signOut(getFirebaseAuth()).catch(() => {});
    }
  } catch (err) {
    console.warn('[auth:teardown] firebase signOut skipped', err);
  }
}

function scheduleDeferredTeardown(expectedGeneration) {
  const task = runDeferredTeardown(expectedGeneration)
    .catch((err) => {
      console.warn('[auth:teardown] deferred cleanup failed', err);
    })
    .finally(() => {
      if (deferredTeardownPromise === task) {
        deferredTeardownPromise = null;
      }
    });
  deferredTeardownPromise = task;
  return task;
}

/** 等待本次 deferred teardown 结束（可选；快速重登靠 generation 守卫） */
export function awaitAuthTeardown() {
  return deferredTeardownPromise || Promise.resolve();
}

/** 登出 / 401：停轮询、清缓存、清充值监听态 */
export function runAuthTeardown() {
  authGeneration += 1;
  const gen = authGeneration;
  scheduleDeferredTeardown(gen);
  teardownFns.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.warn('[auth:teardown] listener failed', err);
    }
  });
}

/** 本地登出：teardown + 清 token */
export function signOutLocalSession() {
  runAuthTeardown();
  clearTokens();
}
